import {
  AGGREGATION_FIELDS,
  type OpenMetadataAggregationBucket,
  type OpenMetadataAggregations,
} from '@/shared/api/openmetadata';
import { ASSET_TYPES } from '@/shared/api/openmetadata';

import type { AssetTreeNode } from './types';

export const ENTITY_TYPE_LABELS: Record<string, string> = {
  database: '데이터베이스',
  dashboard: '대시보드',
  pipeline: '파이프라인',
  topic: '주제',
  mlmodel: 'ML 모델',
  container: '버킷',
  searchIndex: '검색 인덱스',
  apiEndpoint: 'API',
  apiCollection: 'API',
  glossaryTerm: '거버넌스',
  domain: '도메인',
  page: 'Knowledge Center',
  databaseSchema: '데이터베이스 스키마',
  table: '테이블',
  tag: '태그',
};

const GOVERNANCE_SUB_TYPES: Record<string, string> = {
  glossaryTerm: '용어',
  tag: '태그',
  metric: '지표',
};

export const ENTITY_TYPE_CATEGORY_MAP: Record<string, string[]> = {
  database: ['database', 'databaseSchema', 'table'],
  dashboard: ['dashboard', 'dashboardDataModel'],
  pipeline: ['pipeline'],
  topic: ['topic'],
  mlmodel: ['mlmodel'],
  container: ['container'],
  searchIndex: ['searchIndex'],
  apiEndpoint: ['apiEndpoint', 'apiCollection'],
  apiCollection: ['apiEndpoint', 'apiCollection'],
  glossaryTerm: ['glossaryTerm', 'tag', 'metric'],
  domain: ['domain', 'dataProduct'],
  page: ['page'],
  [ASSET_TYPES.EXPERIMENT]: ['run'],
};

const FIRST_LEVEL_ENTITY_TYPES = [
  'database',
  'dashboard',
  'pipeline',
  'topic',
  'mlmodel',
  'container',
  'searchIndex',
  'apiEndpoint',
  'apiCollection',
  'glossaryTerm',
  'domain',
  'page',
];

const ENTITY_TYPE_ORDER: string[] = [
  'database',
  'dashboard',
  'pipeline',
  'topic',
  'mlmodel',
  'container',
  'searchIndex',
  'apiEndpoint',
  'apiCollection',
  'glossaryTerm',
  'domain',
  'page',
];

const ENTITY_TYPE_QUERY_CONFIG: Record<
  string,
  {
    index: string;
    useAggregation: boolean;
    entityTypes?: string[];
    isGovernance?: boolean;
  }
> = {
  database: {
    index: 'database_search_index',
    useAggregation: true,
  },
  dashboard: {
    index: 'dataAsset',
    useAggregation: false,
    entityTypes: ['dashboarddatamodel', 'dashboard'],
  },
  pipeline: {
    index: 'dataAsset',
    useAggregation: false,
    entityTypes: ['pipeline'],
  },
  topic: {
    index: 'topic_search_index',
    useAggregation: true,
  },
  mlmodel: {
    index: 'mlmodel_search_index',
    useAggregation: true,
  },
  container: {
    index: 'container_search_index',
    useAggregation: true,
  },
  searchIndex: {
    index: 'dataAsset',
    useAggregation: false,
    entityTypes: ['searchindex'],
  },
  apiEndpoint: {
    index: 'dataAsset',
    useAggregation: false,
    entityTypes: ['apiendpoint', 'apicollection'],
  },
  apiCollection: {
    index: 'dataAsset',
    useAggregation: false,
    entityTypes: ['apiendpoint', 'apicollection'],
  },
  glossaryTerm: {
    index: 'dataAsset',
    useAggregation: false,
    isGovernance: true,
    entityTypes: ['tag', 'glossaryterm', 'metric'],
  },
  domain: {
    index: 'dataAsset',
    useAggregation: false,
    entityTypes: ['dataproduct'],
  },
  page: {
    index: 'dataAsset',
    useAggregation: false,
    entityTypes: ['page'],
  },
};

const ENTITY_TYPE_INDEX_MAP: Record<string, string> = {
  database: 'database_search_index',
  dashboard: 'dashboard_search_index',
  pipeline: 'pipeline_search_index',
  topic: 'topic_search_index',
  mlmodel: 'mlmodel_search_index',
  container: 'container_search_index',
  searchIndex: 'search_index_search_index',
  apiEndpoint: 'api_endpoint_search_index',
  apiCollection: 'api_collection_search_index',
  glossaryTerm: 'glossary_search_index',
  domain: 'domain_search_index',
  page: 'page_search_index',
  databaseSchema: 'database_schema_search_index',
  table: 'table_search_index',
  tag: 'tag_search_index',
};

export const transformAggregationsToTree = (aggregations: OpenMetadataAggregations): AssetTreeNode[] => {
  const entityTypeBuckets = aggregations[AGGREGATION_FIELDS.ENTITY_TYPE]?.buckets || [];

  const treeNodes: AssetTreeNode[] = entityTypeBuckets
    .filter((bucket: OpenMetadataAggregationBucket) => FIRST_LEVEL_ENTITY_TYPES.includes(bucket.key))
    .sort((a: OpenMetadataAggregationBucket, b: OpenMetadataAggregationBucket) => {
      const indexA = ENTITY_TYPE_ORDER.indexOf(a.key);
      const indexB = ENTITY_TYPE_ORDER.indexOf(b.key);
      if (indexA === -1 && indexB === -1) return 0;
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    })
    .map((bucket: OpenMetadataAggregationBucket) => {
      const entityType = bucket.key;
      const label = ENTITY_TYPE_LABELS[entityType] || entityType;
      const config = ENTITY_TYPE_QUERY_CONFIG[entityType];

      let children: AssetTreeNode[] = [];

      if (config?.isGovernance) {
        const governanceTypes = ['glossaryTerm', 'tag', 'metric'];
        const allBuckets = aggregations[AGGREGATION_FIELDS.ENTITY_TYPE]?.buckets || [];

        children = governanceTypes.map((govType) => {
          const govBucket = allBuckets.find((b) => b.key === govType);
          const count = govBucket?.doc_count || 0;

          return {
            key: `governance-${govType}`,
            title: `${GOVERNANCE_SUB_TYPES[govType] || govType} ${count}`,
            type: 'serviceType' as const,
            serviceType: govType,
            isLeaf: true,
            disabled: count === 0,
            count,
            children: [],
            data: {
              entityTypes: [govType],
              governanceType: govType,
              count,
            },
          };
        });
      }

      return {
        key: `entityType-${entityType}`,
        title: label,
        type: 'entityType' as const,
        serviceType: entityType,
        isLeaf: false,
        children,
        data: {
          entityType,
          index: config?.index || ENTITY_TYPE_INDEX_MAP[entityType] || 'dataAsset',
          useAggregation: config?.useAggregation || false,
          entityTypes: config?.entityTypes,
          isGovernance: config?.isGovernance || false,
        },
      };
    });

  return treeNodes;
};

export { ENTITY_TYPE_INDEX_MAP, ENTITY_TYPE_QUERY_CONFIG, GOVERNANCE_SUB_TYPES };
